import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr, API } from "../../lib/api";
import { PageHeader, btnPrimary, btnTurq, EmptyState, inputCls } from "../../components/bits";
import { Medal, Download, Zap, CheckSquare, Square } from "lucide-react";

const MONTHS_AR = { "01": "يناير", "02": "فبراير", "03": "مارس", "04": "أبريل", "05": "مايو", "06": "يونيو",
  "07": "يوليو", "08": "أغسطس", "09": "سبتمبر", "10": "أكتوبر", "11": "نوفمبر", "12": "ديسمبر" };
const monthAr = (m) => { const [y, mm] = (m || "").split("-"); return `${MONTHS_AR[mm] || mm} ${y || ""}`; };

export default function AdminCertificates() {
  const [coords, setCoords] = useState(null);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/coordinators").then((r) => setCoords(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/admin/certificates").then((r) => setHistory(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    if (selected.includes(id)) {
      setSelected(selected.filter((x) => x !== id));
    } else if (selected.length >= 5) {
      toast.error("الإصدار الجماعي يقبل حتى 5 شهادات في الدفعة الواحدة");
    } else {
      setSelected([...selected, id]);
    }
  };

  const autoTop5 = () => {
    setSelected((coords || []).slice(0, 5).map((c) => c.id));
    toast.success("تم تحديد أعلى 5 منسقين أداءً تلقائياً");
  };

  const issue = async () => {
    if (!selected.length) {
      toast.error("حدد منسقاً واحداً على الأقل");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/certificates/issue", { coordinator_ids: selected, month });
      toast.success(`تم إصدار ${data.issued} شهادة وإرسالها فوراً للمنسقين مع تنبيه الجرس`);
      if (data.skipped?.length) {
        data.skipped.forEach((s) => toast.warning(`${s.name || "منسق"}: ${s.reason}`));
      }
      setSelected([]);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (!coords) return <div className="text-center text-mutext py-20 font-head">جارٍ التحميل …</div>;

  return (
    <div data-testid="admin-certificates-page">
      <PageHeader title="الشهادات الشهرية" sub="فرز آلي حسب الأداء — اعتماد فردي بضغطة زر أو إصدار جماعي حتى 5 شهادات">
        <input data-testid="cert-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={`${inputCls} !w-44`} dir="ltr" />
        <button data-testid="auto-top5-btn" onClick={autoTop5} className={btnTurq}>
          <Zap className="w-4 h-4" /> تحديد الأعلى أداءً تلقائياً (5)
        </button>
        <button data-testid="batch-certificate-issue-btn" onClick={issue} disabled={busy || !selected.length} className={btnPrimary}>
          <Medal className="w-4 h-4" /> إصدار وإرسال جماعي ({selected.length})
        </button>
      </PageHeader>

      <div className="bg-surface rounded-2xl shadow-md border border-brd/50 overflow-auto mb-8">
        <table className="w-full text-sm min-w-[720px]" data-testid="cert-candidates-table">
          <thead>
            <tr className="bg-turq-900 text-white">
              <th className="text-right px-4 py-3 font-head text-xs">تحديد</th>
              <th className="text-right px-4 py-3 font-head text-xs">#</th>
              <th className="text-right px-4 py-3 font-head text-xs">المنسق</th>
              <th className="text-right px-4 py-3 font-head text-xs">المدرسة</th>
              <th className="text-right px-4 py-3 font-head text-xs">مؤشر التمكين</th>
              <th className="text-right px-4 py-3 font-head text-xs">نقاط الأداء</th>
              <th className="text-right px-4 py-3 font-head text-xs">نوعية</th>
              <th className="text-right px-4 py-3 font-head text-xs">انتظام</th>
              <th className="text-right px-4 py-3 font-head text-xs">تفاعل</th>
            </tr>
          </thead>
          <tbody>
            {coords.map((c, i) => {
              const isSel = selected.includes(c.id);
              return (
                <tr key={c.id} data-testid={`cert-candidate-${c.id.slice(0, 8)}`}
                  className={`border-b border-brd/50 last:border-0 cursor-pointer transition-colors ${isSel ? "bg-coral-100 dark:bg-surface2" : "hover:bg-bg"}`}
                  onClick={() => toggle(c.id)}>
                  <td className="px-4 py-3">
                    {isSel ? <CheckSquare className="w-4.5 h-4.5 w-[18px] h-[18px] text-coral-600" /> : <Square className="w-[18px] h-[18px] text-mutext" />}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`w-6 h-6 rounded-full text-[11px] font-black inline-flex items-center justify-center text-white ${i < 3 ? "bg-coral-600" : "bg-turq-900"}`}>{i + 1}</span>
                  </td>
                  <td className="px-4 py-3 font-bold">{c.name}</td>
                  <td className="px-4 py-3 text-[12px]">{c.school_name}</td>
                  <td className="px-4 py-3 font-head font-black text-turq-900 dark:text-turq-300">{c.pct ?? 0}%</td>
                  <td className="px-4 py-3 font-bold text-coral-600">{c.performance}</td>
                  <td className="px-4 py-3">{c.quality ? "✓" : "—"}</td>
                  <td className="px-4 py-3">{c.regular ? "✓" : "—"}</td>
                  <td className="px-4 py-3">{c.engaged ? "✓" : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h2 className="font-head font-bold text-base mb-3">سجل الشهادات الصادرة</h2>
      {history.length === 0 ? (
        <EmptyState testid="cert-history-empty" text="لم تصدر شهادات بعد — حدد المنسقين المتميزين وأصدر أول دفعة" />
      ) : (
        <div className="bg-surface rounded-2xl shadow-md border border-brd/50 overflow-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="cert-history-table">
            <thead>
              <tr className="bg-turq-900 text-white">
                <th className="text-right px-4 py-3 font-head text-xs">المنسق</th>
                <th className="text-right px-4 py-3 font-head text-xs">المدرسة</th>
                <th className="text-right px-4 py-3 font-head text-xs">الشهر</th>
                <th className="text-right px-4 py-3 font-head text-xs">اعتمدها</th>
                <th className="text-right px-4 py-3 font-head text-xs">PDF</th>
              </tr>
            </thead>
            <tbody>
              {history.map((c) => (
                <tr key={c.id} data-testid={`cert-history-${c.id.slice(0, 8)}`} className="border-b border-brd/50 last:border-0 hover:bg-bg transition-colors">
                  <td className="px-4 py-3 font-bold">{c.coordinator_name}</td>
                  <td className="px-4 py-3">{c.school_name}</td>
                  <td className="px-4 py-3">{monthAr(c.month)}</td>
                  <td className="px-4 py-3 text-[11px] text-mutext">{c.issued_by}</td>
                  <td className="px-4 py-3">
                    <button data-testid={`cert-pdf-${c.id.slice(0, 8)}`}
                      onClick={() => window.open(`${API}/admin/certificates/${c.id}/pdf`, "_blank")}
                      className="w-8 h-8 rounded-lg bg-coral-100 dark:bg-surface2 flex items-center justify-center hover:scale-105 transition-transform">
                      <Download className="w-3.5 h-3.5 text-coral-900 dark:text-coral-300" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
